'use client';

import { SnapPastHero } from '@/components/case-study/snap-past-hero';
import { StoriesVideoHero } from '@/components/case-study/stories-video-hero';
import { MicroLabel } from '@/components/case-study/story-card';
import { StoryTile } from '@/components/case-study/story-tile';
import { TestimonialWall } from '@/components/case-study/testimonial-wall';
import { IndexRouteSchema } from '@/components/index-route-schema';
import { useTranslation } from '@/lib/i18n/context';

type CopyKey = keyof typeof import('@/lib/i18n/en').default.customerStories;

type Stat = {
  number: string;
  /** Same value, Arabic-Indic glyphs, for /ar. Never a different figure. */
  numberAr: string;
  textKey: CopyKey;
};

interface StoryData {
  slug: string;
  company: string;
  titleKey: CopyKey;
  /**
   * Customer wordmark from /public/logos. Optional: Aqqo, Trustoo, BONO and
   * TRAVIS have no logo asset yet, so their cards set the company name in
   * type instead. Add the file and this field in the same commit.
   */
  wordmark?: string;
  /**
   * The logo is a coloured shape with a white glyph cut out of it. The wall's
   * ground is dark, so most wordmarks are knocked out to white — but doing
   * that to one of these turns the shape and the glyph the same white and the
   * mark renders as a blank square. Banqy, Nature House, Rewaa and Syarah all
   * have it. Keep their own colours instead; they read fine on near-black.
   */
  wordmarkKeepsColor?: boolean;
  /** `false` for a mark already drawn to sit on a photograph. */
  wordmarkPlate?: boolean;
  /**
   * `square` when the asset is an icon on a square canvas rather than a
   * wordmark on a wide one. Snel Een Taxi's is the only one so far.
   */
  markShape?: 'wide' | 'square';
  /**
   * Optical size correction for this mark, around 1.
   *
   * Every mark is fitted into the same box, which equalises the *bounding
   * box* and nothing else. What a reader sees is the ink, and the ink varies
   * enormously: ZenHR's lockup is nearly square so it hits the box's height
   * cap at 69px wide, while Qoyod's fills all 208px, and Nature House's
   * drawn-in padding differs again from BONO's. Measured, the rendered ink
   * area ran 7.9x from lightest to heaviest — the wall looked like a jumble.
   *
   * So each mark is scaled to a common ink area. Derived, not eyeballed:
   * rasterise the asset, count pixels with alpha > 24 to get the ink fraction
   * of its canvas, multiply by the area the box's `object-contain` fit
   * actually draws it at, and that is the rendered ink area. The target is
   * the median across all marks, and the factor is sqrt(target / area) —
   * square root because area scales with the square of a linear factor. It is
   * clamped to [0.55, 1.9] so no single outlier can blow up or vanish.
   *
   * After the correction the spread is 1.0x. Re-derive it when a mark is
   * added or replaced; a new logo dropped in at 1 will not match.
   */
  markScale?: number;
  stats: Array<Stat>;
  /**
   * Industry and location keys. Carried only by the stories with no cleared
   * figure: their card shows this where the others show a number, so the
   * floor of the card is never empty.
   */
  industryKey?: CopyKey;
  locationKey?: CopyKey;
  /**
   * A single image path, or — when a card needs to reproduce a layered
   * composition — a complete CSS `background-image` value starting with
   * `url(`, whose layers are used verbatim. Stories without cleared imagery
   * simply omit it and take a quiet cell.
   */
  background?: string;
  /**
   * Per-card framing overrides for the background photo. The card sets
   * `bg-cover bg-center`, which suits a wide landscape shot; a photo whose
   * subject is off-centre gets cropped badly by it. Photo cells are square,
   * so `cover` is always height-constrained — which makes `auto <N>%` a clean
   * way to say "cover, zoomed to N%".
   */
  backgroundSize?: string;
  backgroundPosition?: string;
  /** Painted behind the background layers (for art with transparency). */
  backgroundColor?: string;
}

/**
 * Three customers are deliberately absent: IU Group, Sunday Natural and Indie
 * Campers. Each has a drafted story under `_held/` and a signed CSA with a No
 * Publicity clause, and none has written approval on record — see the header
 * of each held page. Their photos and the wall entries are a few lines away
 * once legal clears them (Linear GTM-10); do not add them before that.
 */
const STORIES: Array<StoryData> = [
  {
    slug: 'oto',
    company: 'OTO',
    titleKey:
      'OTO is automating more than 77% of the support with +90% CSAT score',
    // A container ship standing off a port. The film still this replaces was a
    // co-branded frame from OTO's own interview, which put their lockup inside
    // the tile next to the mark the tile already carries.
    background: '/images/customer-stories/wall/oto-shipping.jpg',
    // OTO's own mark, with the black ground knocked out so the white rule and
    // letters sit straight on the photograph. Not inverted — it is already
    // white, and inverting would turn it black.
    wordmark: '/logos/logo-oto-reversed.png',
    wordmarkKeepsColor: true,
    wordmarkPlate: false,
    markShape: 'square',
    markScale: 1.083,
    stats: [
      { number: '77%', numberAr: '٧٧٪', textKey: 'automation' },
      { number: '90%+', numberAr: '+٩٠٪', textKey: 'CSAT score' },
    ],
  },
  {
    slug: 'mollie',
    company: 'Mollie',
    titleKey: 'How Mollie supports 250,000+ businesses across Europe',
    wordmark: '/logos/logo-mollie.svg',
    // Mollie's own photograph, checked in rather than hotlinked off
    // framerusercontent — it is now also the home-page stories card, and a
    // third party's CDN is not somewhere to leave a home-page image.
    background: '/home/stories/mollie-office.jpg',
    markScale: 0.806,
    stats: [
      { number: '70%', numberAr: '٧٠٪', textKey: 'automation' },
      { number: '250K+', numberAr: '+٢٥٠ ألف', textKey: 'Businesses' },
    ],
  },
  {
    slug: 'ticketswap',
    company: 'TicketSwap',
    titleKey:
      'How TicketSwap automated 67% of 19 million user support operations',
    wordmark: '/logos/logo-ticketswap.svg',
    // Flares over a crowd — the night the ticket was for.
    background: '/images/customer-stories/wall/ticketswap-flares.jpg',
    markScale: 1.01,
    stats: [
      { number: '19M', numberAr: '١٩ مليون', textKey: 'users' },
      { number: '78%', numberAr: '٧٨٪', textKey: 'automation' },
    ],
  },
  {
    slug: 'moneygram',
    company: 'MoneyGram',
    titleKey: 'How MoneyGram supports 55M customers across 200+ countries',
    wordmark: '/logos/logo-moneygram.svg',
    // A phone in a hand, which is where fifty-five million of these transfers
    // start. Replaces a hotlink to prnewswire's CDN — a third party's server
    // is not somewhere to leave a customer-wall image, and next/image would
    // not have loaded that host in any case.
    background: '/images/customer-stories/wall/moneygram-phone.jpg',
    // Hold the hand and the screen; a centred crop of the landscape frame
    // drifts onto the second person's knee.
    backgroundPosition: '38% 60%',
    stats: [
      { number: '55M', numberAr: '٥٥ مليون', textKey: 'customers' },
      { number: '80%', numberAr: '٨٠٪', textKey: 'automation' },
    ],
  },
  {
    slug: 'trunkrs',
    company: 'Trunkrs',
    titleKey: 'How Trunkrs automates 80% of delivery support with 4.8/5 rating',
    wordmark: '/logos/logo-trunkrs.svg',
    // A courier loading a Trunkrs van, from their film. The hotlinked SVG this
    // replaces was a flat illustration with nobody in it.
    background: '/home/features/trunkrs-poster.jpg',
    // Hold the courier, not the empty half of the warehouse.
    backgroundPosition: '62% center',
    stats: [
      { number: '80%', numberAr: '٨٠٪', textKey: 'automation' },
      { number: '4.8/5', numberAr: '٤,٨/٥', textKey: 'rating' },
    ],
  },
  {
    // FareHarbor's own drone shot, added on main while this branch was out and
    // kept over the film still this branch had. Their wordmark is burned into
    // the artwork, so the tile carries no separate mark — it would show twice.
    slug: 'fareharbor',
    company: 'FareHarbor',
    titleKey: 'fareharbor_hero_title',
    background: '/images/homepage/testimonials/fareharbor.webp',
    stats: [
      { number: '87%', numberAr: '٨٧٪', textKey: 'Automation rate' },
      { number: '23,000+', numberAr: '+٢٣ ألف', textKey: 'Operators served' },
    ],
  },
  {
    slug: 'more',
    company: 'More.com',
    titleKey: 'How More.com sold 30M tickets across Europe',
    wordmark: '/logos/logo-morecom.svg',
    // A festival crowd under the lights — what More.com sells thirty million
    // tickets to. Replaces a hotlink to more.com's own server that had stopped
    // resolving and that next/image would have refused anyway, the host not
    // being in the remote patterns.
    background: '/images/customer-stories/wall/more-concert.jpg',
    markScale: 0.919,
    stats: [
      { number: '30M', numberAr: '٣٠ مليون', textKey: 'tickets sold' },
      { number: '80%', numberAr: '٨٠٪', textKey: 'automation' },
    ],
  },
  {
    slug: 'syarah',
    company: 'Syarah',
    titleKey: 'syarah_hero_title',
    wordmark: '/logos/logo-syarah.svg',
    wordmarkKeepsColor: true,
    // A still lifted from the customer film itself — Syarah has no cleared
    // press photo. The film is /ar-only; the still is not.
    background: '/images/homepage/testimonials/syarah.jpg',
    // The one figure the story publishes, carried over verbatim from its hero:
    // Syarah's own on-camera statement about its customers' language, not an
    // Open performance claim. Still no automation rate on this card — none is
    // cleared, and the day-1 4.7% is a logged fault, not a result.
    markScale: 1.063,
    stats: [
      {
        number: '95%',
        numberAr: '٩٥٪',
        textKey: 'Customers who prefer Arabic',
      },
    ],
  },
  {
    slug: 'qoyod',
    company: 'Qoyod',
    titleKey: 'How Qoyod automates 77% of support for 25,000+ businesses',
    wordmark: '/logos/logo-qoyod.png',
    // Co-branded frame from Qoyod's film, for the same reason as OTO above.
    // The product render it replaces (a POS terminal) had no one in it.
    background: '/home/stories/qoyod-v2-poster.jpg',
    // Same framing problem as OTO: subject left of centre in a 16:9 frame.
    backgroundPosition: '38% center',
    markScale: 0.727,
    stats: [
      { number: '85%', numberAr: '٨٥٪', textKey: 'automation' },
      { number: '25K+', numberAr: '+٢٥ ألف', textKey: 'Businesses' },
    ],
  },
  {
    slug: 'ocn',
    company: 'OneCar Now',
    titleKey: 'How OCN automates car subscriptions in Mexico',
    wordmark: '/logos/logo-ocn.png',
    background: '/images/homepage/testimonials/ocn.png',
    markScale: 0.889,
    stats: [
      { number: '60%', numberAr: '٦٠٪', textKey: 'automation' },
      { number: '7.5K', numberAr: '٧٥٠٠', textKey: 'vehicles' },
    ],
  },
  {
    slug: 'deonlinedrogist',
    company: 'De Online Drogist',
    titleKey: 'deonlinedrogist_hero_title',
    wordmark: '/logos/logo-deonlinedrogist.svg',
    // Real-world shot of a DeOnlineDrogist JCDecaux placement.
    background: '/images/customer-stories/wall/deonlinedrogist-kitchen.jpg',
    markScale: 1.188,
    stats: [
      { number: '73%', numberAr: '٧٣٪', textKey: 'automation' },
      { number: '25+', numberAr: '+٢٥', textKey: 'actions' },
    ],
  },
  {
    slug: 'cookinglife',
    company: 'CookingLife',
    titleKey: 'cookinglife_hero_title',
    wordmark: '/logos/logo-cookinglife.svg',
    background: '/images/homepage/testimonials/cookinglife.png',
    markScale: 0.916,
    stats: [
      { number: '70%', numberAr: '٧٠٪', textKey: 'automation' },
      { number: '1M+', numberAr: '+مليون', textKey: 'customers' },
    ],
  },
  /* Snel Een Taxi is no longer a customer and has been removed from the wall
   * and from the people row. Their story page, their logo in the marquee and
   * their entry on /helpdesks/intercom are all still live — see the note at
   * the top of this file. */
  {
    // Prêts publishes no performance metrics yet (see the story page comment)
    // and asked us not to — resolution, response times and out-of-hours
    // applications are still being measured. The one figure here is a fact
    // about the rollout, not a result: WhatsApp, email and web, straight from
    // the story's own key results. Add the real numbers in the same commit
    // that lands them.
    slug: 'prets',
    company: 'Prêts',
    titleKey: 'prets_hero_title',
    // No wordmark by choice. The checked-in `logo-prets.svg` is an Arial
    // rendering of the name rather than their mark — it carries its own
    // PLACEHOLDER comment saying so — and the name set in our own face is the
    // better of the two. This is the settled state, not a gap.
    // An installer and his drill — the trade Prets' platform is built around.
    // Replaces a three-layer composite rebuilt from prets.io: cut-out photos
    // and SVG pill callouts on their own section blue. It was the only cell on
    // the wall that was an illustration rather than a photograph, and a
    // multi-layer CSS background is not a `src` next/image can take at all.
    background: '/images/customer-stories/wall/prets-installer.jpg',
    stats: [{ number: '3', numberAr: '٣', textKey: 'Channels covered' }],
  },

  /* The six stories with no cleared figure yet. `stats: []` is deliberate —
   * the card skips the number block entirely rather than showing a
   * placeholder, which would read as a claim. Fill the array in the same
   * commit that lands each customer's approved metrics. */
  {
    slug: 'natuurhuisje',
    company: 'Nature House',
    titleKey: 'natuurhuisje_hero_title',
    industryKey: 'natuurhuisje_industry',
    locationKey: 'natuurhuisje_location',
    // Nature House's own photography, from their site: a cabin terrace on
    // Dutch farmland. Their product is the place, so the place is the picture.
    background: '/images/homepage/testimonials/natuurhuisje.jpg',
    // Cabin sits right of centre in a 3:2 frame; a centred square crop cuts it.
    backgroundPosition: '62% center',
    // Their real wordmark, single-colour, which replaces the placeholder that
    // used to sit at this path — an Arial rendering in a guessed green, by its
    // own admission. One flat fill means it takes the wall's standard white
    // knockout like every other wordmark, so no plate and no square box.
    wordmark: '/logos/logo-natuurhuisje.svg',
    markScale: 0.909,
    stats: [],
  },
  {
    slug: 'wefact',
    company: 'WeFact',
    titleKey: 'wefact_hero_title',
    industryKey: 'wefact_industry',
    locationKey: 'wefact_location',
    wordmark: '/logos/logo-wefact.svg',
    // A portrait WeFact publishes on their own homepage. Square already, so
    // the card crops nothing.
    // Two people and the paperwork between them. Replaces a 600x600 headshot
    // that was too small for the tile and put a white wordmark over a face.
    background: '/images/customer-stories/wall/wefact-office.jpg',
    // Pinned low; the top of the frame is a blown-out window.
    backgroundPosition: '50% 100%',
    markScale: 0.852,
    stats: [],
  },
  {
    slug: 'travis',
    company: 'TRAVIS',
    titleKey: 'travis_hero_title',
    industryKey: 'travis_industry',
    locationKey: 'travis_location',
    // A fuel plaza and trucks — the yards TRAVIS' fleet customers run through.
    // Cropped hard to the lower right of the frame on purpose: the full shot
    // carries a Flying J pylon, a CAT Scales sign and two named carriers on
    // the trailers, and a wall of customer logos is the one place where
    // another company's mark inside a tile reads as a claim about who we work
    // with. Checked in pre-cropped rather than framed with object-position,
    // which could only shift within the square and never lost the pylon.
    background: '/images/customer-stories/wall/travis-truckstop.jpg',
    // Already white on transparent; used as-is rather than inverted.
    wordmark: '/logos/logo-travis.png',
    wordmarkKeepsColor: true,
    wordmarkPlate: false,
    markScale: 0.898,
    stats: [
      {
        number: '~200',
        numberAr: '~٢٠٠',
        textKey: 'Instructions written by the AI',
      },
    ],
  },
  {
    slug: 'trustoo',
    company: 'Trustoo',
    titleKey: 'trustoo_hero_title',
    industryKey: 'trustoo_industry',
    locationKey: 'trustoo_location',
    // A shopper on her phone, which is where a Trustoo review gets written and
    // read. The frame is bright, so the crop is pinned high to put her hair —
    // the darkest mass in it — under the name.
    background: '/images/customer-stories/wall/trustoo-shopper.jpg',
    backgroundPosition: '50% 15%',
    wordmark: '/logos/logo-trustoo.png',
    markScale: 1.025,
    stats: [],
  },
  {
    slug: 'bono',
    company: 'BONO',
    titleKey: 'bono_hero_title',
    industryKey: 'bono_industry',
    locationKey: 'bono_location',
    // Fresh food in a home kitchen, for a health and supplements brand.
    background: '/images/customer-stories/wall/bono-kitchen.jpg',
    // Pinned to the top of the frame so the mark lands on the shelving above
    // her rather than across her face: at 35% she sat dead centre, which is
    // exactly where the wordmark goes, and at 10% it still clipped her
    // hairline. At 0% her face falls in the bottom third.
    backgroundPosition: '50% 0%',
    // White on transparent once the brand ground is knocked out; used as-is.
    wordmark: '/logos/logo-bono.png',
    wordmarkKeepsColor: true,
    wordmarkPlate: false,
    markScale: 1.401,
    stats: [{ number: '21', numberAr: '٢١', textKey: 'European markets' }],
  },
  {
    slug: 'aqqo',
    company: 'Aqqo',
    titleKey: 'aqqo_hero_title',
    industryKey: 'aqqo_industry',
    locationKey: 'aqqo_location',
    // Order paperwork in hand — the back office behind an e-commerce desk.
    background: '/images/customer-stories/wall/aqqo-paperwork.jpg',
    // Pinned high: the lower half of the frame is a bright sheet of paper and
    // the name is set in white.
    backgroundPosition: '50% 8%',
    // White on transparent once the brand ground is knocked out; used as-is.
    wordmark: '/logos/logo-aqqo.png',
    wordmarkKeepsColor: true,
    wordmarkPlate: false,
    markScale: 1.108,
    stats: [
      { number: '148', numberAr: '١٤٨', textKey: 'Help articles synced' },
    ],
  },
  {
    slug: 'banqy',
    company: 'Banqy',
    titleKey: 'banqy_hero_title',
    industryKey: 'banqy_industry',
    locationKey: 'banqy_location',
    // Someone on a call in the street, which is where a Banqy conversation
    // starts.
    background: '/images/customer-stories/wall/banqy-caller.jpg',
    // Hold his face in the wide band; the frame below him is empty pavement.
    backgroundPosition: '50% 28%',
    // Already white on transparent, so it is used as-is rather than inverted.
    wordmark: '/logos/logo-banqy.png',
    wordmarkKeepsColor: true,
    wordmarkPlate: false,
    markScale: 1.192,
    stats: [{ number: '6', numberAr: '٦', textKey: 'Channels in one thread' }],
  },
  {
    slug: 'rewaa',
    company: 'Rewaa',
    titleKey: 'rewaa_hero_title',
    industryKey: 'rewaa_industry',
    locationKey: 'rewaa_location',
    wordmark: '/logos/logo-rewaa.svg',
    wordmarkKeepsColor: true,
    markScale: 1.042,
    stats: [{ number: '4', numberAr: '٤', textKey: 'Systems in one product' }],
  },
  {
    slug: 'foodics',
    company: 'Foodics',
    titleKey: 'foodics_hero_title',
    industryKey: 'foodics_industry',
    locationKey: 'foodics_location',
    // A courier at the door — the last leg of the orders Foodics' restaurants
    // take. The dark doorway sits centre frame, which is what lets the name
    // read in white over it.
    background: '/images/customer-stories/wall/foodics-courier.jpg',
    // Hold the courier; a centred crop of the tall frame fills the tile with
    // paving.
    backgroundPosition: '50% 32%',
    wordmark: '/logos/logo-foodics.png',
    markScale: 0.949,
    stats: [{ number: '35+', numberAr: '+٣٥', textKey: 'Countries served' }],
  },
  {
    slug: 'zenhr',
    company: 'ZenHR',
    titleKey: 'zenhr_hero_title',
    industryKey: 'zenhr_industry',
    locationKey: 'zenhr_location',
    // A desk by the window — the workplace ZenHR administers. The brightest
    // frame on the wall by some way: the blown-out window puts the centre at
    // 149/255 before the logo pool, against a rule of thumb of 130. The pool
    // is what carries it, taking the pixels under the name to an effective
    // 18/255. Anything brighter than this needs a different treatment, not a
    // different crop.
    background: '/images/customer-stories/wall/zenhr-office.jpg',
    // Desk, chair and plant rather than the clutter at the foot of the frame.
    backgroundPosition: '50% 60%',
    // Two-tone in its own colours — dark "zen", white "HR" and ring — so
    // neither keeping the colour nor a white plate works for both halves. It
    // takes the wall's default knockout and reads as one white mark.
    wordmark: '/logos/logo-zenhr.png',
    markScale: 1.775,
    stats: [],
  },
];
/**
 * Three stories run on /ar only.
 *
 * Qoyod, Syarah and Rewaa are Arabic-market customers whose proof — and, for
 * two of them, whose film — is in Arabic. They used to appear on both walls
 * on the argument that "the picture is of a person, not of a language"; the
 * call now is that an English reader has no way into them, so they sit out.
 * Their story pages stay reachable by direct link and stay in the sitemap.
 */
const AR_ONLY = new Set(['qoyod', 'syarah', 'rewaa']);

/**
 * The wall is art-directed, not generated.
 *
 * Two shapes, twelve columns: a wide tile takes eight and a square takes
 * four, so a row is either wide + square or square + square + square. The
 * whole rhythm of the page is where the wide ones fall — roughly every
 * fourth tile, never two in a row, and never one at the very bottom where it
 * would leave a gap under it.
 *
 * A tile earns `wide` for one reason only: it has a number worth setting at
 * 38px. A square tile carries the mark and nothing else, which is what keeps
 * the wall quiet enough for the numbers to land when they come.
 *
 * Anything in STORIES with no slot still renders, as a square at the end, so
 * a new customer is never silently invisible.
 */
/**
 * The named people, for the portrait row above the wall.
 *
 * Eligibility is the same rule the old quote cards used: the testimonial has
 * to be attributed to a named person. The homepage carousel's unattributed
 * quotes do not qualify — a face with no name under it is stock photography,
 * whatever it actually is.
 *
 * Attribution is read from the story's own `*_quote_attribution` key, so the
 * name and title here can never drift from the name and title on the story
 * page. Only two of the five have a portrait so far; the rest set an initial
 * until a photograph lands.
 * TODO(photo): portraits for Joachim de Boer (De Online Drogist) and Davey
 * (CookingLife) — 3:4, head and shoulders, eyes in the top third, in colour.
 * Three of the five are colour portraits now, framed to a common headroom.
 * The remaining two set an initial.
 * TODO(photo): portraits for Joachim de Boer (De Online Drogist) and Davey
 * (CookingLife) — 3:4, head and shoulders, colour, headroom above the head.
 */
const PEOPLE: Array<{
  slug: string;
  company: string;
  attributionKey: CopyKey;
  /** Required, not optional. The row is a row of faces; a card without one is
   *  an initial on a sand field, which is a placeholder wearing a person's
   *  name. Someone with no portrait waits outside the row until they have
   *  one — the type is what stops them being added anyway. */
  portrait: string;
}> = [
  {
    slug: 'prets',
    company: 'Prêts',
    attributionKey: 'prets_quote_attribution',
    portrait: '/images/customer-stories/portraits/koen-visscher-v2.jpg',
  },
  {
    slug: 'trunkrs',
    company: 'Trunkrs',
    attributionKey: 'trunkrs_quote_attribution',
    // Suffixed because the first crop shipped under the unsuffixed name and
    // was cached — by next/image's optimizer and by every browser that had
    // loaded the page. Same path plus new bytes is not a new image to either
    // of them, so a re-crop needs a new URL, not just a new file.
    portrait: '/images/customer-stories/portraits/melvin-trunkrs-v2.jpg',
  },
];

type Slot = { slug: string; wide?: boolean };

const WALL_EN: Array<Slot> = [
  { slug: 'mollie', wide: true },
  { slug: 'moneygram' },

  { slug: 'fareharbor', wide: true },
  { slug: 'ocn' },

  { slug: 'trunkrs', wide: true },
  { slug: 'cookinglife' },

  { slug: 'oto', wide: true },
  { slug: 'natuurhuisje' },

  { slug: 'deonlinedrogist' },
  { slug: 'wefact' },
  { slug: 'trustoo' },

  { slug: 'ticketswap', wide: true },
  { slug: 'foodics' },

  { slug: 'more', wide: true },
  { slug: 'travis' },

  { slug: 'banqy', wide: true },
  { slug: 'prets' },

  { slug: 'zenhr' },
  { slug: 'aqqo' },
  { slug: 'bono' },
];

/**
 * Same twenty, plus the three Arabic-market stories, and reordered so a Saudi
 * reader meets someone recognisable in the first row rather than the fifth.
 * Same stories, same claims — only the order differs.
 */
const WALL_AR: Array<Slot> = [
  { slug: 'qoyod', wide: true },
  { slug: 'syarah' },

  { slug: 'oto', wide: true },
  { slug: 'rewaa' },

  { slug: 'foodics' },
  { slug: 'banqy' },
  { slug: 'moneygram' },

  { slug: 'mollie', wide: true },
  { slug: 'fareharbor' },

  { slug: 'trunkrs', wide: true },
  { slug: 'ocn' },

  { slug: 'cookinglife' },
  { slug: 'natuurhuisje' },
  { slug: 'zenhr' },

  { slug: 'more', wide: true },
  { slug: 'wefact' },

  { slug: 'deonlinedrogist', wide: true },
  { slug: 'trustoo' },

  { slug: 'ticketswap', wide: true },
  { slug: 'travis' },

  { slug: 'aqqo' },
  { slug: 'prets' },
  { slug: 'bono' },
];

const CustomerStoriesPage = () => {
  const { t, isRTL } = useTranslation();

  const wall = isRTL ? WALL_AR : WALL_EN;
  const bySlug = new Map(STORIES.map((story) => [story.slug, story]));

  const visible = STORIES.filter((story) => isRTL || !AR_ONLY.has(story.slug));

  /**
   * One number per tile, and only the first one. The wall used to set two
   * figures per card; at 38px on a photograph a second number is what turns
   * a tile back into a slide.
   */
  const leadStat = (story: StoryData) => {
    const stat = story.stats[0];
    if (!stat) return undefined;
    return {
      value: isRTL ? stat.numberAr : stat.number,
      label: t.customerStories[stat.textKey],
    };
  };

  /**
   * Only a file we host is a picture as far as this wall is concerned.
   *
   * Three backgrounds used to fail this: MoneyGram's and More.com's were
   * hotlinks to a third party's CDN, and the Prêts card was a three-layer CSS
   * composition rather than a photograph. All three now have photographs we
   * host, but the guard stays — it is what keeps a hotlink or a layered value
   * from reaching next/image, which would throw rather than degrade.
   */
  const photoFor = (story: StoryData) =>
    story.background?.startsWith('/') ? story.background : undefined;

  const tileFor = (slot: Slot, i: number) => {
    const story = bySlug.get(slot.slug);
    if (!story) return null;
    if (!isRTL && AR_ONLY.has(story.slug)) return null;

    return (
      <StoryTile
        key={`${story.slug}-${i}`}
        href={`/customer-stories/${story.slug}`}
        title={t.customerStories[story.titleKey]}
        company={story.company}
        wordmark={story.wordmark}
        wordmarkKeepsColor={story.wordmarkKeepsColor}
        wordmarkPlate={story.wordmarkPlate}
        markShape={story.markShape}
        markScale={story.markScale}
        image={photoFor(story)}
        imagePosition={story.backgroundPosition}
        stat={leadStat(story)}
        wide={slot.wide}
        isRTL={isRTL}
      />
    );
  };

  const tiles = wall.map(tileFor);

  const placed = new Set(wall.map((slot) => slot.slug));
  const unplaced = visible
    .filter((story) => !placed.has(story.slug))
    .map((story, i) => tileFor({ slug: story.slug }, 1000 + i));

  return (
    <>
      <IndexRouteSchema route="/customer-stories" />

      {/* The film takes the first screen, with nothing above it. A page
          headline was briefly set on white here and it pushed the stage below
          the fold, which cost the page the one asset nothing else on the site
          has. The h1 lives inside the stage and names whoever is on camera —
          it belongs to the film, and the film is the opening argument. The
          white wall starts where the film ends. */}
      <StoriesVideoHero
        titles={
          isRTL
            ? [
                t.customerStories['qoyod_video_title'],
                t.customerStories['oto_video_title'],
                t.customerStories['syarah_video_title'],
              ]
            : [
                t.customerStories['fareharbor_video_title'],
                t.customerStories['ticketswap_video_title'],
                t.customerStories['moneygram_video_title'],
              ]
        }
        headlines={
          isRTL
            ? [
                t.customerStories['qoyod_video_headline'],
                t.customerStories['oto_video_headline'],
                t.customerStories['syarah_video_headline'],
              ]
            : [
                t.customerStories['fareharbor_video_headline'],
                t.customerStories['ticketswap_video_headline'],
                t.customerStories['moneygram_video_headline'],
              ]
        }
        nowPlaying={t.customerStories['Now playing']}
        playNext={t.customerStories['Play next']}
        watch={t.customerStories['Watch the story']}
      />

      {/* One nudge at the top carries the reader off the film and onto the
          people, rather than making them drag through a viewport of it. */}
      <SnapPastHero targetId="stories-people" />

      {/* The people, before the logos. A reader coming off the film has just
          watched someone talk; the row keeps them with people for one more
          beat before the wall turns into marks and numbers. */}
      <TestimonialWall
        id="stories-people"
        heading={t.customerStories['In their own words']}
        people={PEOPLE.map((person) => ({
          slug: person.slug,
          company: person.company,
          portrait: person.portrait,
          attribution: t.customerStories[person.attributionKey],
        }))}
        isRTL={isRTL}
      />

      {/* The wall. White ground, one gutter, two tile shapes, nothing else. */}
      <section
        data-nav-theme="light"
        className="bg-background pt-16 pb-24 md:pt-24 md:pb-32"
      >
        <div className="container flex flex-col gap-8">
          <div className="flex items-baseline justify-between gap-6">
            <h2 className="font-serif text-foreground text-2xl leading-tight font-normal md:text-[44px] md:tracking-[-0.01em]">
              {t.customerStories['Leaders in customer experience']}
            </h2>
            <MicroLabel
              isRTL={isRTL}
              className="text-muted-foreground shrink-0"
            >
              {isRTL
                ? `${visible.length.toLocaleString('ar-EG')} ${t.customerStories['customer stories']}`
                : `${visible.length} ${t.customerStories['customer stories']}`}
            </MicroLabel>
          </div>

          <div className="grid grid-cols-12 gap-4">
            {tiles}
            {unplaced}
          </div>
        </div>
      </section>
    </>
  );
};

export default CustomerStoriesPage;
